/**
 * ADDVISOR landing page — buy box behavior.
 * The buy box sells across FOUR separate products (Style: Plus/Shade x
 * Quantity: Single/2-Pack), combined into one Style / Color / Quantity
 * picker. Color is normalized ("charcoal" / "ivory") so one control can
 * select the right variant no matter which of the four products is active.
 * No framework dependency; scoped to elements inside [data-av-buybox].
 *
 * Cart integration follows this theme's own protocol (see ProductForm and
 * CartDrawer in the theme bundle): bundle the sections the drawer wants,
 * POST to cart/add.js with those sections, fetch the fresh cart, attach the
 * rendered sections, then dispatch variant:add (opens the drawer) and
 * cart:change (updates drawer contents + cart count). We do NOT try to open
 * the drawer ourselves.
 */
(function () {
  'use strict';

  function initBuyBox(root) {
    var matrixJson = root.querySelector('[data-av-matrix]');
    if (!matrixJson) return;

    var matrix = JSON.parse(matrixJson.textContent);
    var form = root.querySelector('[data-av-form]');
    var styleInputs = root.querySelectorAll('[data-av-style-input]');
    var colorInputs = root.querySelectorAll('[data-av-color-input]');
    var quantityInputs = root.querySelectorAll('[data-av-quantity-input]');
    var priceCurrent = root.querySelector('[data-av-price-current]');
    var priceCompare = root.querySelector('[data-av-price-compare]');
    var submitBtn = root.querySelector('[data-av-submit]');
    var variantIdField = root.querySelector('[data-av-variant-id]');
    var statusEl = root.querySelector('[data-av-status]');
    var packSubtitle = root.querySelector('[data-av-pack-subtitle]');
    var helperNote = root.querySelector('[data-av-helper-note]');

    function checkedValue(inputs) {
      var checked = Array.prototype.find.call(inputs, function (el) {
        return el.checked;
      });
      return checked ? checked.value : null;
    }

    function currentKey() {
      var style = checkedValue(styleInputs);
      var quantity = checkedValue(quantityInputs);
      if (!style || !quantity) return null;
      return style + '_' + (quantity === 'pack' ? 'pack' : 'single');
    }

    function findVariant() {
      var key = currentKey();
      var color = checkedValue(colorInputs);
      if (!key || !matrix[key]) return null;
      return matrix[key].find(function (v) {
        return v.color === color;
      });
    }

    function formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    function shopRoot() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    function updateStyleDependentCopy() {
      var style = checkedValue(styleInputs) || 'plus';
      if (packSubtitle) {
        var savings = packSubtitle.dataset['savings' + style.charAt(0).toUpperCase() + style.slice(1)];
        if (savings) packSubtitle.textContent = savings;
      }
      if (helperNote) {
        var html = helperNote.dataset[style + 'Html'];
        if (html) helperNote.innerHTML = html;
      }
    }

    function updateUI() {
      updateStyleDependentCopy();

      var variant = findVariant();
      if (!variant) {
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = submitBtn.dataset.avUnavailableText || 'Unavailable';
        }
        return;
      }

      if (variantIdField) variantIdField.value = variant.id;

      if (priceCurrent) priceCurrent.textContent = formatMoney(variant.price);
      if (priceCompare) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceCompare.textContent = formatMoney(variant.compare_at_price);
          priceCompare.hidden = false;
        } else {
          priceCompare.hidden = true;
        }
      }

      if (submitBtn) {
        if (variant.available) {
          submitBtn.disabled = false;
          submitBtn.textContent = submitBtn.dataset.avAddText || 'Add to Cart';
        } else {
          submitBtn.disabled = true;
          submitBtn.textContent = submitBtn.dataset.avSoldOutText || 'Sold Out';
        }
      }
    }

    [].concat(
      Array.prototype.slice.call(styleInputs),
      Array.prototype.slice.call(colorInputs),
      Array.prototype.slice.call(quantityInputs)
    ).forEach(function (input) {
      input.addEventListener('change', updateUI);
    });

    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        if (submitBtn && submitBtn.disabled) return;

        var variant = findVariant();
        if (!variant) return;

        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.dataset.avLabel = submitBtn.textContent;
          submitBtn.textContent = submitBtn.dataset.avAddingText || 'Adding…';
        }
        if (statusEl) statusEl.textContent = '';

        var root = shopRoot();

        // Let the theme's cart drawer (and anything else listening) register the
        // sections it wants re-rendered on add. CartDrawer pushes its own section
        // id here, so the cart:change handler below can update itself in place.
        var sectionsToBundle = ['variant-added'];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        }));

        fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest' // lets Shopify check inventory
          },
          body: JSON.stringify({
            id: variant.id,
            quantity: 1,
            sections: sectionsToBundle.join(','),
            sections_url: window.location.pathname
          })
        })
          .then(function (res) {
            if (!res.ok) return res.json().then(function (err) { throw err; });
            return res.json();
          })
          .then(function (addResponse) {
            // Fetch the full cart so the change event carries item_count, items,
            // etc., then attach the rendered sections from the add response.
            return fetch(root + 'cart.js')
              .then(function (r) { return r.json(); })
              .then(function (cart) {
                cart.sections = addResponse.sections;

                if (statusEl) {
                  statusEl.textContent = (submitBtn && submitBtn.dataset.avSuccessText) || 'Added to cart.';
                }

                // Opens the drawer/popover (CartDrawer/CartNotificationDrawer._onVariantAdded).
                document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
                  bubbles: true,
                  detail: {
                    items: addResponse.items || [addResponse],
                    cart: cart
                  }
                }));

                // Updates drawer contents + cart count (CartDrawer._onCartChanged,
                // CartCount). baseEvent 'variant:add' makes the drawer refresh instantly.
                document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
                  bubbles: true,
                  detail: { baseEvent: 'variant:add', cart: cart }
                }));
              });
          })
          .catch(function (err) {
            if (statusEl) statusEl.textContent = (err && err.description) || 'Something went wrong. Please try again.';
          })
          .finally(function () {
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.textContent = submitBtn.dataset.avLabel || submitBtn.dataset.avAddText || 'Add to Cart';
            }
          });
      });
    }

    updateUI();
  }

  /**
   * Sticky bar shows whenever its target (the buy box, by default) is not in
   * view. Decision is driven by getBoundingClientRect via evaluate(), which is
   * the single source of truth so the initial state is correct on load.
   */
  function initStickyBar(bar) {
    if (bar.dataset.avStickyReady === 'true') return; // guard against double-init
    bar.dataset.avStickyReady = 'true';

    var selector = bar.dataset.avStickyTarget || '#av-buy';
    var target = document.querySelector(selector);
    if (!target) return;

    function evaluate() {
      var rect = target.getBoundingClientRect();
      var viewportH = window.innerHeight || document.documentElement.clientHeight;
      var inView = rect.top < viewportH && rect.bottom > 0;
      bar.classList.toggle('is-visible', !inView);
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function () {
        evaluate();
      }, { threshold: [0, 1] });
      observer.observe(target);
    }
    window.addEventListener('scroll', evaluate, { passive: true });
    window.addEventListener('resize', evaluate);

    evaluate();
  }

  function initAll(scope) {
    var context = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    context.querySelectorAll('[data-av-buybox]').forEach(initBuyBox);
    context.querySelectorAll('[data-av-sticky-bar]').forEach(initStickyBar);
  }

  initAll(document);

  // Shopify's theme editor re-renders a section without re-running this script,
  // so the freshly injected markup never gets its handlers attached. Re-init any
  // av-landing widgets inside a section as it loads.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();